import { Injectable, inject } from '@angular/core';
import { Route } from '@angular/router';

import { PrettyUrlsConfig } from '@frontend/sports/common/client-config-data-access';
import { type RouteDataOptions, RouteProcessor } from '@frontend/vanilla/core';
import { kebabCase, template } from 'lodash-es';

export interface PrettyUrlsRoute extends Route {
    children?: PrettyUrlsRoute[] | Route[];
    data?: RouteDataOptions & {
        // used for sports v2 guard
        sport?: number;
        // used for matcher info
        matcherPattern?: string;
        matcherMode?: 'full';
        matcherMapping?: { [key: string]: number };
        matcherParam?: string;
        // used for pretty url
        originalPath?: string;
        product?: string;
    };
}

@Injectable()
export class PrettyUrlRouteProcessor implements RouteProcessor {
    private routeConfig = inject(PrettyUrlsConfig);

    /* Keep for future testing
    test(routeConfig: SportRoute[]): void {
        const log = (route: SportRoute, segements: string[]) => {
            const data = route.data || {};
            const dataSegments = route.path === '' ? segements : [...segements, data.originalPath || 'missing'];

            if (route.children?.length) {
                route.children.forEach((child) => {
                    log(child, dataSegments);
                });
            } else {
                console.log(dataSegments.join('/'));
            }
        };

        routeConfig.forEach((route) => log(route, []));
    } */

    process(route: PrettyUrlsRoute): PrettyUrlsRoute | null {
        // eslint-disable-next-line no-param-reassign
        route.data ??= {};

        if (route.path !== undefined) {
            this.tryUpdateOriginalPath(route, this.stripToken(route.path));
            // eslint-disable-next-line no-param-reassign
            route.path = this.interpolate(route.path);
        }

        if (route.redirectTo) {
            // eslint-disable-next-line no-param-reassign
            route.redirectTo = this.interpolate(route.redirectTo as string);
        }

        if (route.matcher) {
            const includeName = new Set(['region', 'league', 'meeting', 'race']);
            const normalizeName = (param?: string) => kebabCase((param || '').replace('Id', ''));
            const toName = (param?: string) => (includeName.has(normalizeName(param)) ? `${normalizeName(param)}-:${param}` : `:${param}`);

            if (route.data.matcherParam) {
                this.tryUpdateOriginalPath(route, toName(route.data.matcherParam));
            }

            if (route.data.matcherPattern) {
                const useMapping = (segment: string) => segment.includes('(');
                const mapping = Object.keys(route.data.matcherMapping ?? {}).filter((x) => !x.includes('Name'));

                this.tryUpdateOriginalPath(
                    route,
                    this.stripJoin('/', this.stripOptional(route.data.matcherPattern), (segment) =>
                        useMapping(segment) ? toName(mapping.shift()) : this.stripToken(segment),
                    ),
                );

                // eslint-disable-next-line no-param-reassign
                route.data.matcherPattern = this.interpolate(route.data.matcherPattern);
            }
        }

        return route;
    }

    private tryUpdateOriginalPath(route: PrettyUrlsRoute, value: string): void {
        // eslint-disable-next-line no-param-reassign
        route.data = route.data || {};
        if (!route.data.originalPath || this.isWritable(route.data, 'originalPath')) {
            // eslint-disable-next-line no-param-reassign
            route.data.originalPath = value;
        }
    }

    private isWritable<T extends object>(obj: T, key: keyof T): boolean {
        const desc = Object.getOwnPropertyDescriptor(obj, key) || {};

        return Boolean(desc.writable);
    }

    private interpolate(path: string): string {
        return template(path, { interpolate: /{([^}]+?)}/g })({
            ...this.routeConfig.translations,
            formCoupon: 'form-coupon',
        });
    }

    private stripToken(path: string): string {
        if (path === '**') {
            return path;
        }

        return this.stripJoin('/', path, (segment) => this.stripJoin(':', segment, kebabCase));
    }

    private stripOptional(path: string): string {
        return path.replace(/(\([^/]*\)\?|\^|\$)/g, '');
    }

    private stripJoin(token: string, text: string, callback: (node: string) => string): string {
        return text.split(token).map(callback).join(token);
    }
}
